import { Modal, Radio, message } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';
import { AddButton } from '../../../../../lib/components/Buttons/AddButton';
import { InputItem } from '../../../../../lib/components/InputItem';
import {
	isValidCpf,
	sanitizeCpf,
} from '../../../../../lib/utils/cpfValidator';
import { InsertSearchTagDto, Nationality } from '../dto/SearchTagDto';
import {
	createSearchTag,
	findSearchTagByCpf,
	submitWithOverwriteConfirm,
} from '../hooks/SearchTagsApi';
import { AuthorExtraFields } from './AuthorExtraFields';

interface IProps {
	visible: boolean;
	close: () => void;
	insert_success: () => void;
	prefix: string;
	tag_name_in_portuguese: string;
}

const TAG_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.,\/ºª°-]+$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CPF_CHECK_DEBOUNCE_MS = 400;

const BATCH_HINT_BY_PREFIX: Record<string, string> = {
	author: 'João Souza;Maria dos Santos;...',
	kwrd: 'Administrativo;Licitações;...',
	'area-interest': 'Direito Constitucional;Direito Administrativo;...',
	theme: 'Governança;Compliance;...',
	event: 'FCGP 2018;ENGEP 2019;...',
};

export const SearchTagInsertModal: FC<IProps> = ({
	visible,
	close,
	insert_success,
	prefix,
	tag_name_in_portuguese,
}) => {
	const isAuthor = prefix === 'author';

	const [tag_title, set_tag_title] = useState('');
	const [has_document, set_has_document] = useState(false);
	const [nationality, set_nationality] = useState<Nationality>('Brasileiro');
	const [document_value, set_document_value] = useState('');
	const [email, set_email] = useState('');
	const [cpf_check_loading, set_cpf_check_loading] = useState(false);
	const [cpf_conflict, set_cpf_conflict] = useState<{ title: string } | null>(
		null
	);
	const [loading, set_loading] = useState(false);
	const cpf_debounce_ref = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!visible) {
			set_tag_title('');
			set_has_document(false);
			set_nationality('Brasileiro');
			set_document_value('');
			set_email('');
			set_cpf_conflict(null);
			set_cpf_check_loading(false);
		}
	}, [visible]);

	useEffect(() => {
		if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		set_cpf_conflict(null);

		if (!isAuthor || !has_document || nationality !== 'Brasileiro') return;
		const sanitized = sanitizeCpf(document_value);
		if (sanitized.length !== 11 || !isValidCpf(sanitized)) return;

		set_cpf_check_loading(true);
		cpf_debounce_ref.current = setTimeout(async () => {
			try {
				const existing = await findSearchTagByCpf(sanitized);
				if (existing) set_cpf_conflict({ title: existing.title });
			} catch (err) {
				console.error('Erro ao verificar CPF:', err);
			} finally {
				set_cpf_check_loading(false);
			}
		}, CPF_CHECK_DEBOUNCE_MS);

		return () => {
			if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		};
	}, [document_value, isAuthor, has_document, nationality]);

	const sanitizedCpf = sanitizeCpf(document_value);
	const cpfFormatValid =
		nationality === 'Brasileiro' &&
		sanitizedCpf.length === 11 &&
		isValidCpf(sanitizedCpf);
	const emailFilled = email.trim().length > 0;
	const emailFormatValid = EMAIL_REGEX.test(email.trim());
	// BR: CPF obrigatório, email opcional (warning se preencher inválido).
	// EXT: email obrigatório, documento opcional.
	const documentReady =
		nationality === 'Brasileiro' ? cpfFormatValid : true;
	const emailReady =
		nationality === 'Estrangeiro'
			? emailFilled && emailFormatValid
			: !emailFilled || emailFormatValid;
	const authorFormReady =
		isAuthor &&
		has_document &&
		documentReady &&
		emailReady &&
		!cpf_conflict &&
		!cpf_check_loading;

	const submit = async (dto: InsertSearchTagDto) => {
		if (!dto.title) return;

		if (isAuthor && has_document) {
			const name = dto.title.trim();
			if (!name) {
				message.warning('Digite o nome do autor.');
				return;
			}
			if (!TAG_REGEX.test(name)) {
				message.error(`Nome inválido: ${name}`);
				return;
			}
			if (nationality === 'Brasileiro' && !cpfFormatValid) {
				message.error('Informe um CPF válido.');
				return;
			}
			if (nationality === 'Estrangeiro' && !emailFilled) {
				message.error('Email é obrigatório para autores estrangeiros.');
				return;
			}
			if (emailFilled && !emailFormatValid) {
				message.error('Informe um email válido.');
				return;
			}
			if (cpf_conflict) {
				message.error(
					`Já existe um autor cadastrado com este CPF: ${cpf_conflict.title}.`
				);
				return;
			}

			const payload: InsertSearchTagDto = {
				title: name,
				prefix: dto.prefix,
				nationality,
			};
			if (emailFilled) payload.email = email.trim();
			if (nationality === 'Brasileiro') {
				payload.cpf = sanitizedCpf;
			} else if (document_value.trim()) {
				payload.document = document_value.trim();
			}

			set_loading(true);
			const outcome = await submitWithOverwriteConfirm(createSearchTag, payload);
			set_loading(false);

			if (outcome.kind === 'ok') {
				message.success(`Autor "${name}" criado com sucesso!`);
				insert_success();
				close();
				return;
			}
			if (outcome.kind === 'cancelled') {
				message.info(`Cadastro de "${name}" cancelado.`);
				return;
			}
			const err: any = outcome.error;
			const status = err?.response?.status;
			const code = err?.response?.data?.code;
			if (status === 409 && code === 'cpf-conflict') {
				const existing = err?.response?.data?.existing;
				message.error(
					`Já existe um autor cadastrado com este CPF: ${existing?.title || ''}.`
				);
			} else if (status === 409 && code === 'document-conflict') {
				const existing = err?.response?.data?.existing;
				message.error(
					`Já existe um autor cadastrado com este documento: ${existing?.title || ''}.`
				);
			} else if (status === 409 && code === 'email-conflict') {
				const existing = err?.response?.data?.existing;
				message.error(
					`Já existe um autor estrangeiro cadastrado com este email: ${existing?.title || ''}.`
				);
			} else {
				console.error('Erro ao criar autor:', err);
				message.error('Erro ao criar autor.');
			}
			return;
		}

		// Fluxo legado: cadastro em lote por ";" (sem documento/email/nacionalidade)
		const tags = dto.title
			.split(';')
			.map((t) => t.trim())
			.filter((t) => t.length > 0);

		if (tags.length === 0) {
			message.warning('Digite pelo menos uma tag válida.');
			return;
		}

		const invalidTags = tags.filter((tag) => !TAG_REGEX.test(tag));
		if (invalidTags.length > 0) {
			message.error(`As seguintes tags são inválidas: ${invalidTags.join(', ')}`);
			return;
		}

		set_loading(true);
		try {
			for (const tag of tags) {
				const outcome = await submitWithOverwriteConfirm(createSearchTag, {
					title: tag,
					prefix: dto.prefix,
				});
				if (outcome.kind === 'ok') {
					message.success(`Tag "${tag}" criada com sucesso!`);
				} else if (outcome.kind === 'cancelled') {
					message.info(`Cadastro de "${tag}" cancelado.`);
				} else {
					const err: any = outcome.error;
					const status = err?.response?.status;
					const code = err?.response?.data?.code;
					if (status === 409 && code === 'cpf-conflict') {
						const existing = err?.response?.data?.existing;
						message.error(
							`"${tag}" já cadastrado com este CPF: ${existing?.title || ''}.`
						);
					} else {
						console.error(`Erro ao criar tag "${tag}":`, err);
						message.error(`Erro ao criar a tag "${tag}".`);
					}
				}
			}
			insert_success();
			close();
		} finally {
			set_loading(false);
		}
	};

	const submitDisabled =
		!tag_title ||
		loading ||
		(isAuthor && has_document && !authorFormReady);

	return (
		<Modal
			{...{
				visible,
				centered: true,
				closable: true,
				destroyOnClose: true,
				onCancel: () => close(),
				title: `Criar ${tag_name_in_portuguese}`,
				footer: [
					<AddButton
						{...{
							onClick: () => submit({ title: tag_title, prefix }),
							disabled: submitDisabled,
						}}
					/>,
				],
			}}
		>
			{isAuthor && (
				<div className='mt-2 mb-4'>
					<label className='mr-4 font-semibold'>
						Você possui o CPF, Documento ou Email do autor que deseja cadastrar?
					</label>
					<div className='mt-2'>
						<Radio.Group
							value={has_document ? 'yes' : 'no'}
							onChange={(e) => set_has_document(e.target.value === 'yes')}
						>
							<Radio value='no'>Não</Radio>
							<Radio value='yes'>Sim</Radio>
						</Radio.Group>
					</div>
				</div>
			)}

			<InputItem
				{...{
					label: isAuthor && has_document ? 'Nome do autor' : 'Título',
					on_changed: set_tag_title,
					input_item_name: tag_title,
				}}
			/>

			{isAuthor && has_document && (
				<AuthorExtraFields
					nationality={nationality}
					onNationalityChange={(n) => {
						set_nationality(n);
						set_document_value('');
						set_cpf_conflict(null);
					}}
					document={document_value}
					onDocumentChange={set_document_value}
					email={email}
					onEmailChange={set_email}
					cpfCheckLoading={cpf_check_loading}
					cpfConflictTitle={cpf_conflict?.title || null}
					disabled={loading}
				/>
			)}

			{(!isAuthor || !has_document) && (
				<small style={{ color: '#888' }}>
					Digite um ou mais itens.
					<br />
					Para cadastrar vários de uma vez, separe com{' '}
					<b>ponto e vírgula (;)</b>.
					{BATCH_HINT_BY_PREFIX[prefix] && (
						<>
							<br />
							Ex: <i>{BATCH_HINT_BY_PREFIX[prefix]}</i>
						</>
					)}
				</small>
			)}
		</Modal>
	);
};
